import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

// Utility function to clean up old dismissals (keeps localStorage tidy)
const cleanupOldDismissals = () => {
  try {
    const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Keep only dismissals that might still be relevant (last 30 days)
    // This prevents localStorage from growing indefinitely
    const recentDismissals = dismissedBanners.filter(dismissalKey => {
      // Extract timestamp from dismissal key (format: bannerId-timestamp)
      const parts = dismissalKey.split('-');
      if (parts.length < 2) return false; // Invalid format, remove it
      
      try {
        const timestamp = new Date(parts.slice(1).join('-')); // Rejoin in case timestamp has hyphens
        return timestamp > thirtyDaysAgo;
      } catch {
        return false; // Invalid timestamp, remove it
      }
    });
    
    if (recentDismissals.length !== dismissedBanners.length) {
      localStorage.setItem('dismissedBanners', JSON.stringify(recentDismissals));
    }
  } catch (error) {
    // If cleanup fails, just continue - it's not critical
    console.warn('Failed to cleanup old banner dismissals:', error);
  }
};

const DismissableBanner = ({ banner, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    console.log('[BANNER-DEBUG] 🚀 DismissableBanner useEffect triggered:', {
      banner: banner ? `${banner.id} - ${banner.message?.substring(0, 50)}...` : 'null',
      enabled: banner?.enabled
    });

    if (!banner || !banner.enabled) {
      console.log('[BANNER-DEBUG] ❌ Banner is null or disabled, hiding');
      setIsVisible(false);
      return;
    }

    // Clean up old dismissals periodically (only when a banner is being checked)
    cleanupOldDismissals();

    // TEMPORARILY BYPASS DISMISSAL LOGIC FOR DEBUGGING
    console.log('[BANNER-DEBUG] 🟢 FORCE SHOWING BANNER (dismissal bypassed for debug)');
    setIsVisible(true);
    return;

    // Check if banner was previously dismissed (use banner ID + update timestamp for persistence)
    // This ensures that if a banner is edited, everyone sees it again
    const dismissalKey = `${banner.id}-${banner.updatedAt || banner.createdAt}`;
    const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
    
    // Debug logging
    console.log('[BANNER-DEBUG] Banner check:', {
      bannerId: banner.id,
      dismissalKey,
      dismissedBanners,
      isDismissed: dismissedBanners.includes(dismissalKey),
      banner
    });
    
    if (dismissedBanners.includes(dismissalKey)) {
      console.log('[BANNER-DEBUG] Banner was previously dismissed');
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [banner]);

  const handleDismiss = () => {
    setIsVisible(false);
    
    // Don't persist dismissals for preview banners
    if (banner.id === 'preview') {
      if (onDismiss) {
        onDismiss(banner.id);
      }
      return;
    }
    
    // Store dismissal in localStorage using banner ID + timestamp
    const dismissalKey = `${banner.id}-${banner.updatedAt || banner.createdAt}`;
    const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
    if (!dismissedBanners.includes(dismissalKey)) {
      dismissedBanners.push(dismissalKey);
      localStorage.setItem('dismissedBanners', JSON.stringify(dismissedBanners));
    }
    
    if (onDismiss) {
      onDismiss(banner.id);
    }
  };

  if (!banner || !isVisible) {
    return null;
  }

  const getBannerStyles = () => {
    const baseStyles = "text-white shadow-lg";
    
    switch (banner.type) {
      case 'info':
        return `${baseStyles} bg-blue-500`;
      case 'warning':
        return `${baseStyles} bg-yellow-500`;
      case 'error':
        return `${baseStyles} bg-red-500`;
      case 'success':
        return `${baseStyles} bg-green-500`;
      case 'custom':
        return `${baseStyles}`;
      default:
        return `${baseStyles} bg-blue-500`;
    }
  };

  const customStyles = banner.type === 'custom' && banner.customColor 
    ? { backgroundColor: banner.customColor }
    : {};

  return (
    <div
      className={`w-full ${getBannerStyles()} relative overflow-hidden z-50`}
      style={{
        ...customStyles,
        position: 'relative',
        zIndex: 9999,
        minHeight: '60px',
        border: '3px solid lime', // Temporary debug border
        boxShadow: '0 0 20px rgba(0,255,0,0.5)', // Temporary debug glow
        display: 'block' // Force display
      }}
    >
        <div className="max-w-[920px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex-1 mr-4">
            <p className="font-['VT323'] text-[16px] leading-relaxed">
              {banner.message}
            </p>
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-black/10 rounded-full transition-colors"
            title="Dismiss banner"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
        
        {/* Subtle gradient overlay for visual interest */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
      </div>
  );
};

export default DismissableBanner;
